import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiService from '../services/apiService';
import { FaseBadge, useFasesDosVendedores, faseTexto } from '../components/FaseBadge';
import Loading from '../components/Loading';
import './RevisaoPontos.css';   // reaproveita estilos dos filtros (rp-*)
import './FeedbacksAdmin.css';  // reaproveita estilos da tabela (feedbacks-*)

// ── Seletor de período por índice (mesmo padrão de RevisaoPontos/Home) ──
const fmtBR = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
const buildAllDates = () => {
    const now = new Date();
    const cur = new Date(now);
    cur.setDate(cur.getDate() - 182);
    const dates = [];
    while (cur <= now) {
        dates.push(fmtBR(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return dates;
};
const ALL_DATES = buildAllDates();
const TODAY_IDX = ALL_DATES.length - 1;
const idxToISO = (idx) => {
    const d = ALL_DATES[idx];
    if (!d) return '';
    const [dd, mm, yyyy] = d.split('/').map(Number);
    return `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
};
const PRESETS = [
    { label: 'Últimos 7 dias', s: Math.max(0, TODAY_IDX - 6), e: TODAY_IDX },
    { label: 'Últimos 15 dias', s: Math.max(0, TODAY_IDX - 14), e: TODAY_IDX },
    { label: 'Últimos 30 dias', s: Math.max(0, TODAY_IDX - 29), e: TODAY_IDX },
    { label: 'Últimos 90 dias', s: Math.max(0, TODAY_IDX - 89), e: TODAY_IDX },
];

// Opções do filtro de classificação (value = classification_name cru; label pt-BR).
const CLASSIFICATIONS = [
    { v: '', label: 'Todas' },
    { v: 'valid', label: 'Feedback (válido)' },
    { v: 'information', label: 'Informação' },
    { v: 'directions', label: 'Orientação' },
    { v: 'incomplete', label: 'Incompleto' },
    { v: 'brief', label: 'Breve' },
    { v: 'invalid', label: 'Inválido' },
    { v: 'invalid_muted', label: 'Inválido (mudo)' },
];

const RESULT_LIMIT = 500;

// "20491, 20476 20457" -> { ids: [20491, 20476, 20457], invalidos: [] }. Vírgula é o separador
// combinado; espaço, ponto e vírgula e quebra de linha também valem (colar de planilha).
const parseTranscriptionIds = (texto) => {
    const partes = (texto || '').split(/[\s,;]+/).filter(Boolean);
    const invalidos = partes.filter((p) => !/^\d+$/.test(p));
    const ids = [...new Set(partes.filter((p) => /^\d+$/.test(p)).map(Number))];
    return { ids, invalidos };
};

const FeedbacksSearch = ({ user }) => {
    const userId = user?.user_id;

    // Filtros
    const [companies, setCompanies] = useState([]);
    const [stores, setStores] = useState([]);
    const [salesmen, setSalesmen] = useState([]);
    const [subdomain, setSubdomain] = useState('');
    const { fases: fasesVend } = useFasesDosVendedores({ subdomain });
    const [storeId, setStoreId] = useState('');
    const [salesmanId, setSalesmanId] = useState('');
    const [classification, setClassification] = useState('');
    const [originalClassification, setOriginalClassification] = useState('');
    const [transcriptionIdsText, setTranscriptionIdsText] = useState('');
    const { ids: transcriptionIds } = parseTranscriptionIds(transcriptionIdsText);
    const porId = transcriptionIds.length > 0;
    const [startIdx, setStartIdx] = useState(Math.max(0, TODAY_IDX - 14)); // default: últimos 15 dias
    const [endIdx, setEndIdx] = useState(TODAY_IDX);

    // Dados
    const [feedbacks, setFeedbacks] = useState(null); // null = ainda não buscou
    const [selos, setSelos] = useState({});           // transcription_id -> fase selada
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Áudio dos comentários
    const [playingAudios, setPlayingAudios] = useState({});
    const [loadingAudios, setLoadingAudios] = useState({});
    const audioRefs = useRef({});

    // Modal de transcrição
    const [transcriptionModal, setTranscriptionModal] = useState({ open: false, feedbackId: null });
    const [transcriptionData, setTranscriptionData] = useState(null);
    const [loadingTranscription, setLoadingTranscription] = useState(false);
    const [copiedField, setCopiedField] = useState(null);

    // Player de áudio do feedback. `redacted` faz parte da chave: os dois botões (original e
    // anonimizado) são independentes, e clicar num não deve parecer que parou o outro.
    const [audioPlayer, setAudioPlayer] = useState({ feedbackId: null, redacted: false, url: null, error: null, loading: false });
    const feedbackAudioRef = useRef(null);

    // Escopo do admin: limitado (core.admin_companies) só vê transcrição com contexto
    // anonimizado e só ouve áudio anonimizado. Default restrito até a resposta chegar —
    // é a API que decide, isto aqui só evita oferecer o que ela vai negar.
    const [restrictedAdmin, setRestrictedAdmin] = useState(true);
    useEffect(() => {
        (async () => {
            const r = await apiService.adminScope();
            setRestrictedAdmin(!!r.data?.restricted);
        })();
    }, []);

    // ── Carregar empresas ──
    useEffect(() => {
        (async () => {
            try {
                const data = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(data) ? data : []);
            } catch (e) {
                setError('Erro ao carregar empresas');
            }
        })();
    }, []);

    // ── Carregar lojas ao trocar empresa ──
    useEffect(() => {
        setStores([]); setStoreId(''); setSalesmanId(''); setSalesmen([]);
        if (!subdomain) return;
        (async () => {
            try {
                const st = await apiService.promptEditorGetStores(subdomain);
                setStores(Array.isArray(st) ? st : []);
            } catch (e) { /* silencioso */ }
        })();
    }, [subdomain]);

    // ── Carregar vendedores ao escolher empresa/loja ──
    useEffect(() => {
        if (!subdomain) { setSalesmen([]); return; }
        (async () => {
            try {
                const r = await apiService.pointsSalesmen({ userId, subdomain, store_id: storeId || undefined });
                setSalesmen(r.salesmen || []);
            } catch (e) { /* silencioso */ }
        })();
    }, [subdomain, storeId, userId]);

    const buscar = useCallback(async () => {
        if (!subdomain) { setError('Selecione uma empresa'); return; }
        const { ids, invalidos } = parseTranscriptionIds(transcriptionIdsText);
        if (invalidos.length) { setError(`transcription_id inválido: ${invalidos.join(', ')}`); return; }
        setLoading(true);
        setError('');
        try {
            const result = await apiService.searchFeedbacks({
                subdomain,
                store_id: storeId ? Number(storeId) : undefined,
                salesman_id: salesmanId ? Number(salesmanId) : undefined,
                start: idxToISO(startIdx),
                end: idxToISO(endIdx),
                classification: classification || undefined,
                original_classification: originalClassification || undefined,
                // com ids a API ignora o período (start/end continuam indo: o proxy exige)
                transcription_ids: ids.length ? ids : undefined,
                limit: RESULT_LIMIT,
            });
            if (result.success) {
                const lista = result.data.feedbacks || [];
                setFeedbacks(lista);

                /**
                 * Fase de CADA atendimento, pelo selo — não a fase de hoje do vendedor.
                 *
                 * Esta tela lista atendimento, e a fase que importa aqui é a que valeu no dia: um
                 * atendimento de março pontuou pela régua de março. Mostrar a fase de hoje em todas
                 * as linhas diria o contrário, e é exatamente a confusão que o selo existe para
                 * evitar. A fase ATUAL do vendedor continua no filtro de vendedor, onde ela é a
                 * informação certa ("em que fase ele está agora").
                 *
                 * Uma chamada para a página inteira (até 500 linhas), e falha não derruba a busca:
                 * o resultado já está na tela, a fase é adicional.
                 */
                const tids = lista.map((f) => f.transcription_id).filter(Boolean);
                if (tids.length) {
                    try {
                        const s = await apiService.phaseSealsBatch({ subdomain, transcription_ids: tids });
                        setSelos(s?.data?.seals || {});
                    } catch (e) { setSelos({}); }
                } else {
                    setSelos({});
                }
            } else {
                setError(result.message || 'Erro ao buscar feedbacks');
                setFeedbacks([]);
                setSelos({});
            }
        } catch (e) {
            setError('Erro ao buscar feedbacks: ' + (e.message || 'desconhecido'));
            setFeedbacks([]);
            setSelos({});
        } finally {
            setLoading(false);
        }
    }, [subdomain, storeId, salesmanId, startIdx, endIdx, classification, originalClassification, transcriptionIdsText]);

    // ── Helpers de renderização (mesmos da tela de feedbacks) ──
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString('pt-BR', {
                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
            });
        } catch (e) { return 'Data inválida'; }
    };

    const formatAudioLength = (seconds) => {
        if (!seconds || seconds <= 0) return 'N/A';
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const renderFeedbackText = (fb) => {
        if (fb && typeof fb === 'object' && !Array.isArray(fb)) {
            const { good, bad } = fb;
            if (good == null && bad == null) return JSON.stringify(fb);
            return (
                <>
                    {good && (
                        <div className="feedback-good">
                            <strong>✅ Pontos positivos</strong>
                            <div style={{ whiteSpace: 'pre-line' }}>{good}</div>
                        </div>
                    )}
                    {bad && (
                        <div className="feedback-bad" style={{ marginTop: good ? 8 : 0 }}>
                            <strong>⚠️ Pontos de atenção</strong>
                            <div style={{ whiteSpace: 'pre-line' }}>{bad}</div>
                        </div>
                    )}
                </>
            );
        }
        return fb || '-';
    };

    const getReadIcon = (read) => (read === 1
        ? <span className="icon read" title="Lido">✅</span>
        : <span className="icon unread" title="Não lido">🙈</span>);

    const getLikesIcon = (likes) => {
        if (likes === null || likes === undefined) return <span className="icon no-reaction" title="Sem reação">-</span>;
        if (likes === 1) return <span className="icon thumbs-up" title="Curtiu">👍</span>;
        return <span className="icon thumbs-down" title="Não curtiu">👎</span>;
    };

    const handleAudioPlay = async (audioId, bucketName) => {
        try {
            const actualId = audioId.toString().includes('__') ? audioId.toString().split('__').pop() : audioId;
            if (playingAudios[audioId]) {
                const a = audioRefs.current[audioId];
                if (a) { a.pause(); setPlayingAudios(prev => ({ ...prev, [audioId]: false })); }
                return;
            }
            // Pausar os demais
            Object.keys(audioRefs.current).forEach(id => {
                const a = audioRefs.current[id];
                if (a && !a.paused) { a.pause(); setPlayingAudios(prev => ({ ...prev, [id]: false })); }
            });
            let audio = audioRefs.current[audioId];
            if (!audio) {
                audio = new Audio();
                audioRefs.current[audioId] = audio;
                audio.addEventListener('ended', () => setPlayingAudios(prev => ({ ...prev, [audioId]: false })));
                audio.addEventListener('error', () => {
                    setPlayingAudios(prev => ({ ...prev, [audioId]: false }));
                    setLoadingAudios(prev => ({ ...prev, [audioId]: false }));
                });
                audio.addEventListener('canplay', () => setLoadingAudios(prev => ({ ...prev, [audioId]: false })));
            }
            setLoadingAudios(prev => ({ ...prev, [audioId]: true }));
            audio.src = `/api/audio-proxy/${actualId}${bucketName ? `?bucket_name=${encodeURIComponent(bucketName)}` : ''}`;
            await audio.play();
            setPlayingAudios(prev => ({ ...prev, [audioId]: true }));
        } catch (e) {
            setLoadingAudios(prev => ({ ...prev, [audioId]: false }));
        }
    };

    const renderAudioComments = (audioCommentIds, bucketName) => {
        if (!audioCommentIds || audioCommentIds.length === 0) return <span>-</span>;
        return (
            <div className="audio-comments-list">
                {audioCommentIds.map((audioId) => {
                    const displayId = audioId.toString().includes('__') ? audioId.toString().split('__').pop() : audioId;
                    const displayDate = audioId.toString().includes('__') ? audioId.toString().split('__')[0] : '';
                    return (
                        <div key={audioId} className="audio-comment-item">
                            <span className="audio-id">#{displayId}</span>
                            <span className="audio-date">{displayDate}</span>
                            <button
                                className={`audio-play-button ${playingAudios[audioId] ? 'playing' : ''}`}
                                onClick={() => handleAudioPlay(audioId, bucketName)}
                                disabled={loadingAudios[audioId]}
                                title={playingAudios[audioId] ? 'Pausar áudio' : 'Reproduzir áudio'}
                            >
                                {loadingAudios[audioId] ? '⏳' : playingAudios[audioId] ? '⏸️' : '▶️'}
                            </button>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Cleanup de áudios ao desmontar
    useEffect(() => {
        const refs = audioRefs.current;
        return () => Object.values(refs).forEach(a => { if (a && !a.paused) a.pause(); });
    }, []);

    // ── Transcrição ──
    const handleOpenTranscription = async (feedbackId, bucketName) => {
        setTranscriptionModal({ open: true, feedbackId });
        setTranscriptionData(null);
        setLoadingTranscription(true);
        setCopiedField(null);
        try {
            const result = await apiService.getTranscription(userId, bucketName, feedbackId);
            setTranscriptionData(result.success ? result.data : { error: result.message || 'Erro ao buscar transcrição' });
        } catch (err) {
            setTranscriptionData({ error: err.message || 'Erro ao buscar transcrição' });
        } finally {
            setLoadingTranscription(false);
        }
    };
    const handleCloseTranscription = () => {
        setTranscriptionModal({ open: false, feedbackId: null });
        setTranscriptionData(null);
        setCopiedField(null);
    };
    const handleCopyText = async (text, field) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else {
                const ta = document.createElement('textarea');
                ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
                document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
            }
            setCopiedField(field);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (e) { /* silencioso */ }
    };

    // ── Áudio do feedback ──
    const handleListenAudio = async (feedbackId, bucketName, redacted = false) => {
        const mesmoBotao = audioPlayer.feedbackId === feedbackId && audioPlayer.redacted === redacted;
        if (mesmoBotao && audioPlayer.url) {
            setAudioPlayer({ feedbackId: null, redacted: false, url: null, error: null, loading: false });
            return;
        }
        setAudioPlayer({ feedbackId, redacted, url: null, error: null, loading: true });
        try {
            const result = await apiService.listenAudioFeedback(feedbackId, userId, bucketName, redacted);
            if (result.success) {
                setAudioPlayer({ feedbackId, redacted, url: result.data.presigned_url, error: null, loading: false });
            } else {
                setAudioPlayer({ feedbackId, redacted, url: null, error: result.message || 'Erro ao buscar áudio', loading: false });
            }
        } catch (err) {
            setAudioPlayer({ feedbackId, redacted, url: null, error: err.message || 'Erro ao buscar áudio', loading: false });
        }
    };
    useEffect(() => {
        if (audioPlayer.url && feedbackAudioRef.current) {
            feedbackAudioRef.current.load();
            feedbackAudioRef.current.play().catch(() => { /* browser may block */ });
        }
    }, [audioPlayer.url]);

    return (
        <div className="rp-page feedbacks-admin">
            <h1 className="rp-title">💭 Feedbacks / Atendimentos</h1>

            {/* Filtros */}
            <div className="rp-filters">
                <div className="rp-field">
                    <label>Empresa</label>
                    <select value={subdomain} onChange={(e) => setSubdomain(e.target.value)}>
                        <option value="">— Selecione —</option>
                        {companies.map((c) => <option key={c.subdomain} value={c.subdomain}>{c.name}</option>)}
                    </select>
                </div>
                <div className="rp-field">
                    <label>Loja</label>
                    <select value={storeId} onChange={(e) => { setStoreId(e.target.value); setSalesmanId(''); }} disabled={!subdomain}>
                        <option value="">Todas</option>
                        {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                    </select>
                </div>
                <div className="rp-field">
                    <label>Vendedor</label>
                    <select value={salesmanId} onChange={(e) => setSalesmanId(e.target.value)} disabled={!subdomain}>
                        <option value="">Todos</option>
                        {salesmen.map((s) => (
                            <option key={s.salesman_id} value={s.salesman_id}>
                                {s.salesman_name}{s.is_active ? '' : ' (inativo)'}{faseTexto(fasesVend[String(s.salesman_id)], { comDia: false })}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="rp-field">
                    <label>Classificação</label>
                    <select value={classification} onChange={(e) => setClassification(e.target.value)}>
                        {CLASSIFICATIONS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                    </select>
                </div>
                <div className="rp-field">
                    <label>Classificação original</label>
                    <select value={originalClassification} onChange={(e) => setOriginalClassification(e.target.value)}>
                        {CLASSIFICATIONS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                    </select>
                </div>
                <div className="rp-field">
                    <label>Atendimentos (transcription_id)</label>
                    <input
                        type="text"
                        value={transcriptionIdsText}
                        onChange={(e) => setTranscriptionIdsText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && subdomain && !loading) buscar(); }}
                        placeholder="ex.: 20491, 20476"
                        title="Um ou mais transcription_id separados por vírgula. Com ids, o período não filtra."
                    />
                    {porId && <small style={{ color: '#667' }}>{transcriptionIds.length} id(s) — o período não filtra</small>}
                </div>
                <div className="rp-field">
                    <label>Início</label>
                    <select value={startIdx} onChange={(e) => setStartIdx(+e.target.value)} disabled={porId}>
                        {ALL_DATES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                </div>
                <div className="rp-field">
                    <label>Fim</label>
                    <select value={endIdx} onChange={(e) => setEndIdx(+e.target.value)} disabled={porId}>
                        {ALL_DATES.map((d, i) => <option key={i} value={i} disabled={i < startIdx}>{d}</option>)}
                    </select>
                </div>
                <div className="rp-field rp-presets">
                    <label>Atalhos</label>
                    <div className="rp-preset-btns">
                        {PRESETS.map((p, i) => (
                            <button key={i} type="button" disabled={porId}
                                className={`rp-pill${startIdx === p.s && endIdx === p.e ? ' active' : ''}`}
                                onClick={() => { setStartIdx(p.s); setEndIdx(p.e); }}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="rp-filters-actions">
                    <button className="rp-btn rp-btn-primary" onClick={buscar} disabled={loading || !subdomain}>
                        {loading ? 'Buscando…' : '🔎 Buscar'}
                    </button>
                </div>
            </div>

            {error && <div className="rp-toast rp-toast-error" style={{ position: 'static', margin: '8px 0' }}>{error}</div>}

            {loading ? (
                <Loading message="Carregando feedbacks..." />
            ) : feedbacks === null ? (
                <p className="rp-hint" style={{ padding: '8px 4px', color: '#667' }}>
                    Selecione os filtros e clique em Buscar.
                </p>
            ) : feedbacks.length === 0 ? (
                <div className="no-feedbacks"><p>Nenhum feedback encontrado para os filtros selecionados.</p></div>
            ) : (
                <>
                    <p className="total-info" style={{ margin: '8px 4px' }}>
                        Total: {feedbacks.length}{feedbacks.length >= RESULT_LIMIT ? ` (limitado aos ${RESULT_LIMIT} mais recentes — refine os filtros)` : ''}
                    </p>
                    <div className="feedbacks-table-container">
                        <table className="feedbacks-table">
                            <thead>
                                <tr>
                                    <th>ID (feedback_id)</th>
                                    <th>Data/Hora</th>
                                    <th>Duração do Áudio</th>
                                    <th>Duração da Transcrição</th>
                                    <th>Vendedor</th>
                                    <th>Nota</th>
                                    <th>Classificação</th>
                                    <th>Classif. original</th>
                                    <th>Pipeline</th>
                                    <th>Feedback</th>
                                    <th>Lido</th>
                                    <th>Likes</th>
                                    <th>Comentários</th>
                                    <th>Áudios</th>
                                </tr>
                            </thead>
                            <tbody>
                                {feedbacks.map((feedback) => (
                                    <tr key={`${feedback.subdomain}-${feedback.id}`} className="feedback-row">
                                        <td className="feedback-id">{feedback.id}</td>
                                        <td className="feedback-date">{formatDate(feedback.start)}</td>
                                        <td className="feedback-duration" title="Duração real do áudio gravado">{formatAudioLength(feedback.audio_duration)}</td>
                                        <td className="feedback-duration" title="Até o fim da última fala transcrita">{formatAudioLength(feedback.transcription_length)}</td>
                                        <td className="feedback-salesman">
                                            {feedback.salesman}{feedback.salesman_is_active ? '' : ' (inativo)'}
                                            {(() => {
                                                const sel = selos[String(feedback.transcription_id)];
                                                return sel && sel.kind === 'phase' ? <FaseBadge fase={sel} mostrarDia={false} /> : null;
                                            })()}
                                        </td>
                                        <td className="feedback-rating">{feedback.nota_geral || '-'}</td>
                                        <td className="feedback-classification">{feedback.classification_name || '—'}</td>
                                        <td className="feedback-classification">{feedback.original_classification_name || '—'}</td>
                                        <td className="feedback-classification">{feedback.pipeline || '—'}</td>
                                        <td className="feedback-text">
                                            <div className="feedback-content">{renderFeedbackText(feedback.feedback)}</div>
                                            <button
                                                className="transcription-button"
                                                onClick={() => handleOpenTranscription(feedback.id, feedback.bucket_name)}
                                                title="Ver transcrição"
                                            >
                                                📝 Transcrição
                                            </button>
                                            {!restrictedAdmin && (
                                                <button
                                                    className="transcription-button"
                                                    style={{ marginLeft: '8px' }}
                                                    onClick={() => handleListenAudio(feedback.id, feedback.bucket_name, false)}
                                                    title="Ouvir áudio original"
                                                    disabled={audioPlayer.loading && audioPlayer.feedbackId === feedback.id && !audioPlayer.redacted}
                                                >
                                                    {audioPlayer.loading && audioPlayer.feedbackId === feedback.id && !audioPlayer.redacted
                                                        ? '⏳ Carregando...'
                                                        : audioPlayer.feedbackId === feedback.id && !audioPlayer.redacted && audioPlayer.url
                                                            ? '⏹️ Parar Áudio'
                                                            : '🔊 Ouvir Áudio'}
                                                </button>
                                            )}
                                            <button
                                                className="transcription-button"
                                                style={{ marginLeft: '8px' }}
                                                onClick={() => handleListenAudio(feedback.id, feedback.bucket_name, true)}
                                                title="Ouvir áudio com os trechos fora do atendimento silenciados"
                                                disabled={audioPlayer.loading && audioPlayer.feedbackId === feedback.id && audioPlayer.redacted}
                                            >
                                                {audioPlayer.loading && audioPlayer.feedbackId === feedback.id && audioPlayer.redacted
                                                    ? '⏳ Carregando...'
                                                    : audioPlayer.feedbackId === feedback.id && audioPlayer.redacted && audioPlayer.url
                                                        ? '⏹️ Parar Áudio Anonimizado'
                                                        : '🔇 Ouvir Áudio Anonimizado'}
                                            </button>
                                            {audioPlayer.feedbackId === feedback.id && audioPlayer.url && (
                                                <div className="audio-player-inline">
                                                    <audio
                                                        ref={feedbackAudioRef}
                                                        controls
                                                        src={audioPlayer.url}
                                                        style={{ width: '100%', marginTop: '6px' }}
                                                        onEnded={() => setAudioPlayer({ feedbackId: null, url: null, error: null, loading: false })}
                                                    />
                                                </div>
                                            )}
                                            {audioPlayer.feedbackId === feedback.id && audioPlayer.error && (
                                                <div className="audio-error-message" style={{ color: '#dc3545', fontSize: '12px', marginTop: '6px', padding: '6px 8px', background: '#fff5f5', borderRadius: '4px', border: '1px solid #f5c6cb' }}>
                                                    {audioPlayer.error}
                                                </div>
                                            )}
                                        </td>
                                        <td className="feedback-read">{getReadIcon(feedback.read)}</td>
                                        <td className="feedback-likes">{getLikesIcon(feedback.likes)}</td>
                                        <td className="feedback-comments">
                                            {/* textarea, e não <div>: o comentário é texto do vendedor e pode vir
                                                com palavra longa sem espaço (link colado, texto emendado). Como a
                                                tabela não usa table-layout: fixed, o width do <td> é só sugestão e
                                                conteúdo que não quebra alargava a coluna, desmontando o espaçamento
                                                da tabela inteira. O textarea é elemento substituído: o tamanho dele
                                                não depende do conteúdo. É somente leitura — a tela não salva
                                                comentário. */}
                                            {feedback.comments ? (
                                                <textarea
                                                    className="feedback-comments-box"
                                                    value={feedback.comments}
                                                    readOnly
                                                    rows={5}
                                                    title="Comentário do vendedor (somente leitura)"
                                                />
                                            ) : (
                                                <div className="feedback-content">-</div>
                                            )}
                                        </td>
                                        <td className="feedback-audio-comments">
                                            {renderAudioComments(feedback.audio_comment_ids, feedback.bucket_name)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Modal de Transcrição */}
            {transcriptionModal.open && (
                <div className="transcription-modal-overlay" onClick={handleCloseTranscription}>
                    <div className="transcription-modal" onClick={e => e.stopPropagation()}>
                        <div className="transcription-modal-header">
                            <h2>Transcrição - Feedback #{transcriptionModal.feedbackId}</h2>
                            <button className="transcription-modal-close" onClick={handleCloseTranscription}>✕</button>
                        </div>
                        <div className="transcription-modal-body">
                            {loadingTranscription ? (
                                <div className="transcription-loading">Carregando transcrição...</div>
                            ) : transcriptionData?.error ? (
                                <div className="transcription-error">{transcriptionData.error}</div>
                            ) : transcriptionData ? (
                                <>
                                    {transcriptionData.restricted_admin && (
                                        <div className="transcription-note" style={{ marginBottom: '12px', opacity: 0.8 }}>
                                            Seu acesso mostra apenas a transcrição com contexto anonimizado.
                                        </div>
                                    )}
                                    {!transcriptionData.restricted_admin && (
                                    <>
                                    <div className="transcription-field">
                                        <div className="transcription-field-header">
                                            <label>Transcrição Pura</label>
                                            <button
                                                className={`transcription-copy-button ${copiedField === 'pure' ? 'copied' : ''}`}
                                                onClick={() => handleCopyText(transcriptionData.audio_transcription || '', 'pure')}
                                            >
                                                {copiedField === 'pure' ? '✅ Copiado' : '📋 Copiar'}
                                            </button>
                                        </div>
                                        <textarea
                                            className="transcription-textarea"
                                            readOnly
                                            value={transcriptionData.audio_transcription || 'Sem transcrição disponível'}
                                        />
                                    </div>
                                    <div className="transcription-field">
                                        <div className="transcription-field-header">
                                            <label>Transcrição Anonimizada</label>
                                            <button
                                                className={`transcription-copy-button ${copiedField === 'anon' ? 'copied' : ''}`}
                                                onClick={() => handleCopyText(transcriptionData.anon_transcript || '', 'anon')}
                                            >
                                                {copiedField === 'anon' ? '✅ Copiado' : '📋 Copiar'}
                                            </button>
                                        </div>
                                        <textarea
                                            className="transcription-textarea"
                                            readOnly
                                            value={transcriptionData.anon_transcript || 'Sem transcrição disponível'}
                                        />
                                    </div>
                                    </>
                                    )}
                                    <div className="transcription-field">
                                        <div className="transcription-field-header">
                                            <label>
                                                Transcrição com Contexto Anonimizado
                                                {typeof transcriptionData.anon_context_spans?.length === 'number' && (
                                                    <span style={{ marginLeft: '8px', fontWeight: 'normal', opacity: 0.75 }}>
                                                        ({transcriptionData.anon_context_spans.length} trecho(s) removido(s))
                                                    </span>
                                                )}
                                            </label>
                                            <button
                                                className={`transcription-copy-button ${copiedField === 'ctx' ? 'copied' : ''}`}
                                                onClick={() => handleCopyText(transcriptionData.anon_context_transcript || '', 'ctx')}
                                            >
                                                {copiedField === 'ctx' ? '✅ Copiado' : '📋 Copiar'}
                                            </button>
                                        </div>
                                        <textarea
                                            className="transcription-textarea"
                                            readOnly
                                            value={transcriptionData.anon_context_transcript
                                                || 'Ainda não processada para este atendimento'}
                                        />
                                        {transcriptionData.anon_context_spans?.length > 0 && (
                                            <details style={{ marginTop: '8px' }} open>
                                                <summary style={{ cursor: 'pointer' }}>
                                                    O que foi removido, e por quê
                                                </summary>
                                                <ul style={{ margin: '8px 0 0 0', paddingLeft: '18px' }}>
                                                    {transcriptionData.anon_context_spans.map((sp) => (
                                                        <li key={sp.i} style={{ marginBottom: '4px' }}>
                                                            <code>{sp.ts}</code> <em>{sp.reason}</em>
                                                            {sp.text ? <> — {sp.text}</> : null}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </details>
                                        )}
                                    </div>
                                </>
                            ) : null}
                        </div>
                        <div className="transcription-modal-footer">
                            <button className="transcription-modal-close-btn" onClick={handleCloseTranscription}>Fechar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeedbacksSearch;
